// Centro de ayuda: explica cada modulo con pantallas ilustradas (los datos
// sensibles van tapados con ███). Cada usuario ve SOLO la ayuda de los
// modulos que tiene permitidos.

import { api } from "../api";
import { iconoRuta, iconoAyuda, iconoPan } from "../iconos";
import type { Sesion } from "../../lib/types";

// Un paso o un tip puede pedir su propio permiso: dentro de una seccion hay
// cosas que no todos pueden hacer (anular pedidos, por ejemplo). Si es un texto
// suelto, lo ve cualquiera que vea la seccion.
type Linea = string | { permiso: string; texto: string };

interface SeccionAyuda {
  permiso: string | null; // null = todos; '__admin' = solo admin
  hash: string; // para el icono
  titulo: string;
  queEs: string;
  // La pantalla ilustrada puede depender de los permisos: no tiene sentido
  // dibujarle el boton de anular a quien no lo va a ver nunca.
  mock: string | ((tiene: (permiso: string) => boolean) => string);
  pasos: Linea[];
  tips: Linea[];
}

const T = (txt: string) => `<span class="tapado">${txt}</span>`;

const SECCIONES: SeccionAyuda[] = [
  {
    permiso: "pedido",
    hash: "#/pedido",
    titulo: "Pedido",
    queEs:
      "Es la pantalla principal: se toma el pedido de un cliente para entregar (por defecto) manana. Al confirmar, el pedido suma a la produccion del dia y genera la COMPRA en la cuenta corriente del cliente.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2">
          <span class="mock-campo">Cliente: ${T("XXXXXXXXXX")}</span>
          <span class="mock-campo">Empresa: Torremolinos</span>
          <span class="mock-campo">Entrega: 17/07/2026</span>
          <span class="mock-btn-sec">Sugerir cantidades</span>
        </div>
        <div class="mock-campo">🔍 flauta</div>
        <div class="rounded-lg border border-crema-200 p-2 text-masa-500">
          <div><b class="text-masa-700">Pan Flauta Chica x 3</b> <span class="text-masa-400">#8005</span> — en Pan Frances</div>
          <div><b class="text-masa-700">Pan Flauta Kg</b> <span class="text-masa-400">#8001</span> — en Pan Frances <span class="text-amber-700">no esta en la lista</span></div>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">▾ BIZCOCHOS</div>
          <div class="flex items-center justify-between gap-2">
            <span>Bizcochos Kg <span class="text-masa-400">#1001 · x1</span></span>
            <span class="mock-campo">$ ${T("999")}</span>
            <span class="flex items-center gap-1"><span class="mock-btn-sec">−</span><span class="mock-campo">3</span><span class="mock-btn-sec">+</span></span>
            <span class="font-bold">$ ${T("9.999")}</span>
          </div>
          <div class="mt-1 text-center text-masa-400">+ Agregar articulo</div>
        </div>
        <div class="flex items-center justify-between border-t border-crema-200 pt-2">
          <span>TOTAL ESTIMADO <b>$ ${T("9.999")}</b></span>
          <span class="flex gap-1"><span class="mock-btn-sec">Ver solo cargados</span><span class="mock-btn-sec">Limpiar</span><span class="mock-btn">Confirmar pedido</span></span>
        </div>
      </div>`,
    pasos: [
      "Elegi el <b>cliente</b>: se cargan solos los articulos de SU lista de precios, agrupados por categoria (tocá una categoria para abrirla).",
      "Elegi la <b>empresa emisora</b> de la comanda (obligatorio).",
      "Cargá las <b>cantidades</b> con los botones − / + (suman de a 1) o escribiendo el numero a mano — <b>acepta fracciones</b> (ej: 2.5 kg). El total se actualiza abajo.",
      "Si el cliente pide 'lo de siempre', toca <b>Sugerir cantidades</b>: se precarga su ultimo pedido y ajustas lo que cambie.",
      "Tocá <b>Confirmar pedido</b> → se guarda, impacta produccion y cuenta corriente, y aparece el <b>remito</b>. Al imprimir salen <b>dos vias</b>: Via 1 (cliente) y Via 2 (control).",
    ],
    tips: [
      "El pedido en curso <b>no se pierde</b>: si cambias de pantalla o se recarga el navegador, sigue donde lo dejaste.",
      "<b>Buscador</b> (arriba de las categorias): escribi una palabra (ej: <i>flauta</i>) y te muestra las coincidencias diciendo en que categoria esta cada una. Tocando una, la pantalla abre esa categoria, baja hasta el articulo, lo resalta y te deja el cursor en la cantidad. Tambien anda con las flechitas y Enter.",
      "Si el articulo que buscas <b>no esta en la lista</b> del cliente, sale marcado en ambar y al tocarlo se abre el dialogo de agregar/quitar de su categoria, ya filtrado por lo que escribiste.",
      "<b>Agregar / quitar articulos</b> (al pie de cada categoria) abre la lista completa con casillas: tildas lo que va y destildas lo que no, todo de una vez.",
      "El boton del tachito tambien <b>saca</b> un articulo directo desde la grilla (no borra nada del catalogo).",
      "Solo el <b>administrador</b> puede ajustar el precio de una linea (queda marcado en ambar; la flechita vuelve al precio de lista).",
    ],
  },
  {
    permiso: "produccion",
    hash: "#/produccion",
    titulo: "Produccion",
    queEs:
      "Muestra lo que la cuadra tiene que producir para una fecha: el resumen de pedidos del dia, el detalle por cliente y el total a hornear por articulo, area por area, ya convertido a unidades.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex gap-2"><span class="mock-campo">Fecha: 17/07/2026</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">RESUMEN DE PEDIDOS DEL DIA</div>
          <div class="flex justify-between"><span>${T("XXXXXXX")} <span class="text-masa-400">#12</span></span><span>$ ${T("9.999")}</span></div>
          <div class="flex justify-between border-t border-crema-100 font-bold"><span>TOTAL DEL DIA</span><span>$ ${T("99.999")}</span></div>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">DETALLE POR CLIENTE</div>
          <table class="w-full"><tr class="text-masa-400"><td>Articulo</td><td>${T("Cli A")}</td><td>${T("Cli B")}</td><td class="font-bold">Totales</td></tr>
          <tr><td>Galleta de Campana</td><td>32</td><td>130</td><td class="font-bold">162</td></tr></table>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">PANADERIA</div>
          <table class="w-full"><tr class="text-masa-400"><td>Articulo</td><td class="font-bold">Paquetes</td><td class="font-bold">Unidades</td></tr>
          <tr><td class="font-bold">GALLETA DE CAMPANA</td><td class="font-bold">86</td><td class="font-bold">333</td></tr>
          <tr><td class="pl-3 text-masa-500">Pan Galleta Campana x4</td><td class="text-masa-500">66 x 4</td><td class="text-masa-500">264</td></tr></table>
        </div>
      </div>`,
    pasos: [
      "Elegi la <b>fecha</b> (por defecto muestra manana): la pantalla se actualiza sola, no hay que apretar nada.",
      "Arriba esta el <b>resumen de pedidos</b>: toca un cliente para ver el detalle de su pedido e imprimirlo.",
      "Abajo esta el bloque grande <b>PRODUCCION</b>, con la fecha de entrega en el titulo: articulo, <b>paquetes</b> y <b>unidades</b> (unidades = paquetes x unidades por paquete) — es la hoja para la cuadra.",
      "Los articulos que comparten <b>grupo de produccion</b> salen en una sola linea con el total en unidades (toda la galleta de campana junta) y abajo, en chico, de que envases salio. Los grupos se arman en Productos.",
      "Justo abajo del resumen esta el <b>detalle por cliente</b>: una fila por articulo, una columna por cliente y la columna de totales, en unidades. Ahi <b>no</b> se redondea: es lo que lleva cada cliente, tal cual se le facturo.",
      "Si un pedido viene fraccionado (2,5 kilos de flauta = 7,5 flautas), la hoja del horno <b>redondea para arriba</b> (8) y avisa abajo el valor exacto. El pedido, el remito y la cuenta del cliente siguen con los 2,5 kilos y su precio.",
      "Dos botones de imprimir: el de <b>PRODUCCION</b> saca <b>solo</b> la hoja de la cuadra (sin clientes ni precios); el de <b>arriba</b> imprime toda la pagina, con el resumen y el detalle por cliente.",
    ],
    tips: ["Si anulas un pedido, desaparece solo de la produccion de esa fecha."],
  },
  {
    permiso: "pedido",
    hash: "#/pedidos",
    titulo: "Pedidos",
    queEs:
      "La lista de todos los pedidos tomados, con su estado, para consultarlos y filtrarlos. Corregir y anular un pedido confirmado se hacen desde aca, cada uno con su permiso (<b>Editar pedidos</b> y <b>Anular pedidos</b>): abajo aparecen los pasos de lo que vos podes hacer.",
    mock: (tiene) => `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2">
          <span class="mock-campo">Cliente: Todos</span><span class="mock-campo">Desde: 01/07/2026</span><span class="mock-campo">Hasta: 29/07/2026</span>
          <span class="mock-btn-sec">Dia</span><span class="mock-btn-sec">Mes</span>
          ${tiene("pedidos_anular") ? `<span class="mock-btn" style="background:#dc2626">Habilitar anulacion</span>` : ""}
        </div>
        <table class="w-full">
          <tr class="text-masa-400"><td>Fecha</td><td>Pedido</td><td>Cliente</td><td>Total</td><td>Estado</td></tr>
          <tr><td>29/07/2026</td><td class="text-masa-400">#14</td><td>${T("XXXXXXX")}</td><td>$ ${T("9.999")}</td><td><span class="text-green-700">CONFIRMADO</span></td>${tiene("pedidos_editar") ? "<td>✎</td>" : ""}</tr>
          <tr class="text-masa-400"><td>28/07/2026</td><td>#13</td><td class="line-through">${T("XXXXXX")}</td><td class="line-through">$ ${T("9.999")}</td><td><span class="text-red-700">ANULADO</span></td></tr>
        </table>
      </div>`,
    pasos: [
      "Filtra por <b>cliente</b> y por fecha (<b>Dia / Semana / Mes / Todo</b>, o poniendo Desde y Hasta a mano).",
      "La tabla muestra fecha de entrega, numero de pedido, cliente, empresa, total y <b>estado</b>. Los anulados salen tachados con un cartel rojo.",
      "Tocando el <b>numero de pedido</b> (#12) se abre el <b>resumen</b>: que articulos lleva, con la cantidad, el precio y el total. Es solo para mirar; el remito para imprimir esta en Produccion.",
      "Abajo aparece cuantos pedidos hay confirmados y el <b>total del periodo</b> (los anulados no suman).",
      { permiso: "pedidos_editar", texto: "El <b>lapiz</b> abre el pedido para <b>editarlo</b>: se pueden cambiar cantidades, articulos, fecha, empresa y descuento — el cliente no. Al guardar se actualiza la produccion y la compra en la cuenta." },
      { permiso: "pedidos_anular", texto: "Para anular hay que <b>habilitar la anulacion</b> primero: hasta que no toques ese boton no aparece ningun boton de anular. Es a proposito, para no anular de casualidad." },
      { permiso: "pedidos_anular", texto: "Con la anulacion habilitada, toca <b>Anular</b> en el pedido. Se abre un cartel con el cliente, la fecha y el total: revisa que sea el correcto y confirma." },
    ],
    tips: [
      { permiso: "pedidos_anular", texto: "Anular <b>no borra nada</b>: el pedido queda guardado como ANULADO y su compra sigue apareciendo en la cuenta del cliente, tachada, sin sumar al saldo." },
      { permiso: "pedidos_anular", texto: "El pedido anulado sale solo de la produccion de su fecha." },
      { permiso: "pedidos_editar", texto: "Si <b>no sos admin</b>, los precios del pedido <b>no se tocan</b>: cada linea queda con el precio que ya tenia, aunque lo cambies en la pantalla. Corregir una cantidad o una fecha nunca mueve precios." },
      { permiso: "pedidos_editar", texto: "Corregir es mejor que anular y rehacer: el pedido conserva su numero y en <b>Auditoria</b> queda quien lo cambio, cuando y de cuanto a cuanto." },
    ],
  },
  {
    permiso: "cuentas",
    hash: "#/cuentas",
    titulo: "Cuentas",
    queEs:
      "La cuenta corriente de cada cliente: compras, devoluciones, faltantes y pagos, con el saldo siempre calculado. Desde aca se imprime el estado de cuenta (factura interna).",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Cliente: ${T("XXXXXXXXXX")}</span><span class="mock-campo">Desde 01/07</span><span class="mock-campo">Hasta 16/07</span><span class="mock-btn-sec">Dia</span><span class="mock-btn-sec">Semana</span><span class="mock-btn-sec">Mes</span><span class="mock-btn-sec">Con detalles</span><span class="mock-btn-sec">Imprimir</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Fecha</td><td>Detalle</td><td>Compra</td><td>Pago</td><td>Saldo</td></tr>
          <tr><td>16/07</td><td>COMPRA <span class="text-masa-400">#12</span></td><td>$ ${T("9.999")}</td><td></td><td>$ ${T("9.999")}</td></tr>
          <tr><td>16/07</td><td>PAGO</td><td></td><td>$ ${T("5.000")}</td><td>$ ${T("4.999")}</td></tr>
        </table>
        <div class="flex justify-end gap-2 border-t border-crema-200 pt-1"><span>Saldo actual: <b>$ ${T("4.999")}</b></span></div>
        <div class="flex gap-2"><span class="mock-campo">Pago ▾</span><span class="mock-campo">$ …</span><span class="mock-btn">Registrar</span></div>
      </div>`,
    pasos: [
      "Elegi el <b>cliente</b>: se ve cada movimiento con el saldo acumulado fila por fila.",
      "Filtra el periodo con <b>Desde/Hasta</b> o los botones <b>Dia / Semana / Mes / Todo</b>.",
      "Para cobrar: en <b>Registrar movimiento</b> elegi <b>Pago</b> y escribi el monto. Para <b>Devolucion</b> o <b>Faltante</b> elegi los articulos abajo (como en un pedido) y el monto se calcula solo con la lista del cliente.",
      "La cuenta empieza sin detalles: descripcion, numero de pedido, importes y saldo. Toca <b>Con detalles</b> para mostrar los articulos, cantidades y subtotales debajo de cada compra. El boton pasa a decir <b>Sin detalles</b>: tocalo para ocultarlos.",
      "Tocá <b>Imprimir</b> para imprimir o guardar como PDF: sale igual que en pantalla, con o sin detalles y con el periodo elegido.",
      "Para <b>anular un pedido</b> desde aca (solo el admin): toca <b>Habilitar anulacion</b> y aparece un boton <b>Anular pedido</b> al lado del numero de cada compra. Es lo mismo que se puede hacer en la pestana <b>Pedidos</b>.",
    ],
    tips: [
      "El saldo <b>nunca se edita a mano</b>: siempre es compras − devoluciones − faltantes − pagos.",
      "El modo de detalle se conserva al cambiar cliente o fechas. Al salir de Cuentas y volver a entrar, empieza otra vez sin detalles.",
      "Cada pedido confirmado genera su COMPRA solo. Si lo anulas, la compra queda <b>tachada con el cartel ANULADO</b> y deja de contar en el saldo, pero <b>no se borra</b>: se ve que ese pedido existio.",
      "Una compra anulada no muestra articulos en la impresion con detalle.",
    ],
  },
  {
    permiso: "listas",
    hash: "#/listas",
    titulo: "Lista de precios",
    queEs:
      "Las 30 listas de precios (Lista01–Lista30). Cada cliente tiene UNA asignada. Aca se ven, se imprimen con el logo, se editan y se crean listas nuevas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Por cliente ▾</span><span class="mock-campo">o por lista ▾</span><span class="mock-btn-sec">+ Nueva lista</span><span class="mock-btn-sec">Editar</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 flex items-center justify-between"><span class="font-bold">🥖 Lista de precios</span><span class="text-masa-400">Lista22 — ${T("XXXXX")}</span></div>
          <table class="w-full"><tr class="text-masa-400"><td>Cod</td><td>Articulo</td><td>Emp</td><td>Precio</td></tr>
            <tr><td>1001</td><td>Bizcochos Kg</td><td>x1</td><td class="font-bold">$ ${T("999")}</td></tr>
          </table>
        </div>
      </div>`,
    pasos: [
      "Elegi <b>por cliente</b> (muestra la lista que ese cliente tiene asignada) o <b>por lista</b> directamente.",
      "En modo <b>Ver</b>: la hoja sale limpia, agrupada por categoria, lista para imprimir con el logo.",
      "Tocá <b>Editar</b> para cambiar precios (quedan en ambar), quitar articulos (tachito = precio 0) o agregar articulos que faltan.",
      "Tocá <b>Guardar cambios</b>: se aplican todos juntos.",
      "<b>+ Nueva lista</b> crea una lista vacia desde cero y la abre en modo editar.",
    ],
    tips: [
      "OJO: editar una lista afecta a <b>todos los clientes</b> que la usan (la app te lo avisa con un cartel ambar).",
      "Los pedidos ya confirmados NO cambian: el precio quedo congelado al confirmarlos.",
    ],
  },
  {
    permiso: "subas",
    hash: "#/subas",
    titulo: "Subas",
    queEs:
      "El aumento de precios: aplicas un % a una o varias listas y los precios nuevos quedan redondeados a pesos enteros. Con vista previa obligatoria e historial de todas las subas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">☑ Lista10</span><span class="mock-campo">☑ Lista22</span><span class="mock-campo">% 5</span><span class="mock-btn">Vista previa</span></div>
        <div class="rounded-lg bg-amber-50 p-2 text-amber-800">Vista previa +5%: se modifican ${T("99")} precios en 2 listas. Los pedidos confirmados NO cambian.</div>
        <table class="w-full"><tr class="text-masa-400"><td>Articulo</td><td>Actual</td><td>Nuevo</td></tr>
          <tr><td>Bizcochos Kg</td><td>$ ${T("226")}</td><td class="font-bold">$ ${T("237")}</td></tr>
        </table>
        <div class="flex gap-2"><span class="mock-btn">Aplicar suba +5%</span><span class="mock-btn-sec">Cancelar</span></div>
      </div>`,
    pasos: [
      "Tilda las <b>listas</b> a aumentar (o 'Todas').",
      "Escribi el <b>% de aumento</b> y, si queres, una nota (ej: 'suba de harina julio').",
      "Tocá <b>Vista previa</b>: se ve cada precio actual → nuevo, SIN cambiar nada todavia.",
      "Revisá y toca <b>Aplicar suba</b>: los precios se actualizan y la suba queda en el <b>historial</b> (fecha, lista, %, nota).",
    ],
    tips: [
      "Los precios nuevos quedan <b>redondeados a pesos enteros</b> (sin decimales), como siempre se hizo.",
      "Nunca se aplica nada sin vista previa: si te arrepentis, Cancelar.",
    ],
  },
  {
    permiso: "informes",
    hash: "#/informes",
    titulo: "Informes",
    queEs:
      "El resumen de ventas: cuanto compro cada cliente, cuanto se vendio por dia y cuales son los productos mas vendidos, en el periodo que elijas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Desde 01/07</span><span class="mock-campo">Hasta 16/07</span><span class="mock-btn-sec">Dia</span><span class="mock-btn-sec">Mes</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-lg border border-crema-200 p-2"><div class="font-bold text-masa-700">VENTAS POR CLIENTE</div>
            <div class="flex justify-between"><span>${T("XXXXXXX")}</span><span>$ ${T("9.999")}</span></div>
            <div class="flex justify-between border-t border-crema-100 font-bold"><span>TOTAL</span><span>$ ${T("99.999")}</span></div></div>
          <div class="rounded-lg border border-crema-200 p-2"><div class="font-bold text-masa-700">MAS VENDIDOS</div>
            <div class="flex justify-between"><span>Bizcochos Kg</span><span>${T("99")} bultos</span></div></div>
        </div>
      </div>`,
    pasos: [
      "Elegi el periodo con <b>Desde/Hasta</b> o los botones rapidos <b>Dia / Semana / Mes / Todo</b>.",
      "Se muestran tres cuadros: <b>ventas por cliente</b> (con el total general), <b>ventas por dia</b> y <b>productos mas vendidos</b>.",
      "Tocá <b>Imprimir</b> si necesitas la hoja.",
    ],
    tips: ["Solo cuentan los pedidos CONFIRMADOS (los anulados no suman)."],
  },
  {
    permiso: "clientes",
    hash: "#/clientes",
    titulo: "Clientes",
    queEs:
      "El padron de clientes: nombre, contacto, telefono, direccion y — lo mas importante — que lista de precios tiene asignada cada uno.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-masa-400">⊕ NUEVO CLIENTE</div>
        <table class="w-full"><tr class="text-masa-400"><td>Nombre</td><td>Lista</td><td>Telefono</td><td></td></tr>
          <tr><td>CU0010</td><td>${T("XXXXXXXXXX")}</td><td>Lista22</td><td>${T("099999999")}</td><td>✎</td></tr>
        </table>
      </div>`,
    pasos: [
      "Para dar de alta: abri <b>⊕ Nuevo cliente</b>, poné el nombre y la <b>lista de precios</b> que le corresponde. No hay que inventar ningun codigo.",
      "Para modificar: toca el <b>lapiz</b> de la fila → podes cambiar todo, incluida la lista, y activar/desactivar el cliente.",
    ],
    tips: [
      
      "Cambiarle la lista a un cliente cambia los precios de sus <b>proximos</b> pedidos (los viejos no se tocan).",
      "Los clientes no se borran: se <b>desactivan</b> (conservan su historial y su cuenta).",
    ],
  },
  {
    permiso: "productos",
    hash: "#/productos",
    titulo: "Productos",
    queEs:
      "El catalogo completo (~120 articulos): codigo, descripcion, categoria, area de produccion, unidades por paquete, grupo de produccion e IVA.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex gap-2"><span class="mock-campo">🔍 Buscar…</span><span class="text-masa-400">⊕ NUEVO PRODUCTO</span><span class="mock-btn-sec">Grupos de produccion</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Codigo</td><td>Descripcion</td><td>Categoria</td><td>Grupo</td><td>Un./paq.</td><td>IVA</td><td></td></tr>
          <tr><td>1001</td><td>Bizcochos Kg</td><td>Bizcochos</td><td class="text-masa-400">—</td><td>x1</td><td>0%</td><td>✎</td></tr>
          <tr><td>2021</td><td>Pan Galleta Campana x4</td><td>Casero</td><td>Galleta de Campana</td><td>x4</td><td>0%</td><td>✎</td></tr>
          <tr><td>8001</td><td>Pan Flauta Kg</td><td>Pan Frances</td><td>Flauta</td><td>x3</td><td>0%</td><td>✎</td></tr>
        </table>
        <div class="text-masa-400">Mostrando 1–15 de 121 · ‹ ›</div>
      </div>`,
    pasos: [
      "<b>Buscar</b> filtra por nombre al instante.",
      "Alta: <b>⊕ Nuevo producto</b> — al elegir la categoria, el <b>codigo se sugiere solo</b> (siguiente de la familia, ej. Bizcochos → 1xxx).",
      "Edicion: el <b>lapiz</b> abre todo (descripcion, categoria, area, unidades por paquete, grupo, IVA, activo).",
      "<b>Grupos de produccion</b> (boton arriba a la derecha): crear, renombrar o borrar grupos y ver que articulos tiene cada uno. Un articulo se mete en un grupo desde su lapiz.",
    ],
    tips: [
      "Las <b>unidades por paquete</b> son clave: produccion multiplica paquetes × unidades por paquete para saber cuanto hornear. Un pack x8 lleva 8; el kilo de flauta lleva 3 (en un kilo entran 3 flautas). No cambia el precio: se sigue facturando por paquete o por kilo.",
      "El <b>grupo de produccion</b> junta los distintos envases del mismo pan (campana suelta, x4 y x8) en un unico total para el panadero. Si manana creas otro envase, lo asignas al grupo y suma solo.",
      "El codigo duplicado se <b>bloquea</b> con un aviso.",
    ],
  },
  {
    permiso: "categorias",
    hash: "#/categorias",
    titulo: "Categorias",
    queEs:
      "Las familias de productos (Bizcochos, Pan Frances, Roscas…): crear, renombrar, borrar, asignarles el prefijo de codigo y aplicar IVA masivo a todos sus productos.",
    mock: `
      <div class="mock space-y-2">
        <table class="w-full"><tr class="text-masa-400"><td>Nombre</td><td>Prefijo</td><td>Productos</td><td>IVA</td><td></td></tr>
          <tr><td>Bizcochos</td><td>1</td><td>10</td><td><span class="mock-campo">0% ▾</span> <span class="mock-btn-sec">Aplicar</span></td><td>✎ 🗑</td></tr>
        </table>
      </div>`,
    pasos: [
      "El numero de la columna <b>Productos</b> se toca: abre la lista de articulos de esa categoria y podes <b>moverlos</b> a otra.",
      "La columna <b>IVA</b> aplica una tasa (0/10/22%) a TODOS los productos de la categoria de una vez ('mixto' = tienen tasas distintas).",
      "El <b>prefijo</b> define la familia de codigos (Bizcochos=1 → 1001, 1002…): asi el alta de productos sugiere el codigo solo.",
    ],
    tips: ["No se puede borrar una categoria con productos: primero movelos a otra."],
  },
  {
    permiso: "__admin",
    hash: "#/empresas",
    titulo: "Empresas emisoras",
    queEs:
      "Solo para el administrador: las panaderias del grupo cuyos datos salen impresos en el remito. Al tomar un pedido se elige cual de ellas lo emite.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-masa-400">⊕ Nueva empresa</div>
        <table class="w-full">
          <tr class="text-masa-400"><td>Nombre</td><td>Direccion</td><td>Telefono</td><td>Estado</td><td></td></tr>
          <tr><td>${T("XXXXXXXXX")}</td><td>${T("XXXXXXXXX")}</td><td>${T("2999 9999")}</td><td><span class="text-green-700">ACTIVA</span></td><td>✏ 🗑</td></tr>
          <tr class="text-masa-400"><td class="line-through">${T("XXXXXX")}</td><td></td><td></td><td><span class="text-red-700">INACTIVA</span></td><td>✏</td></tr>
        </table>
      </div>`,
    pasos: [
      "Abri <b>⊕ Nueva empresa</b> y completa el <b>nombre</b> (lo unico obligatorio), la direccion, la ciudad y el telefono: eso es lo que sale en el encabezado del remito.",
      "El <b>lapiz</b> edita los datos. Desde ahi tambien se puede volver a <b>activar</b> una empresa dada de baja.",
      "El <b>tachito</b> la desactiva: deja de aparecer al elegir la empresa de un pedido.",
    ],
    tips: [
      "Las empresas <b>no se borran</b> de verdad: los pedidos y remitos viejos las siguen necesitando, por eso solo se desactivan.",
      "<b>Siempre tiene que quedar una activa</b>: sin empresas no se puede confirmar ningun pedido, asi que el sistema no deja desactivar la ultima.",
    ],
  },
  {
    permiso: "__admin",
    hash: "#/usuarios",
    titulo: "Usuarios",
    queEs:
      "Solo para el administrador: crear los usuarios del sistema y elegir con tildes que puede usar cada uno. El menu de cada usuario muestra unicamente lo permitido.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-masa-400">⊕ NUEVO USUARIO</div>
        <div class="flex flex-wrap gap-1"><span class="mock-campo">☑ Pedido</span><span class="mock-campo">☑ Editar pedidos</span><span class="mock-campo">☐ Anular pedidos</span><span class="mock-campo">☑ Produccion</span><span class="mock-campo">☐ Lista de precios</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Usuario</td><td>Tipo</td><td>Permisos</td><td>Estado</td><td></td></tr>
          <tr><td>${T("encargada")}</td><td>usuario</td><td>Pedido, Editar pedidos, Produccion</td><td>Activo</td><td>✎</td></tr>
          <tr><td>${T("repartidor")}</td><td>usuario</td><td>Pedido, Produccion</td><td>Activo</td><td>✎</td></tr>
        </table>
      </div>`,
    pasos: [
      "Alta: usuario + clave (min. 8) + tildar los <b>permisos</b> (o marcar 'Es administrador' para que vea todo).",
      "El <b>lapiz</b> permite cambiar permisos, <b>desactivar</b> al usuario o <b>resetear su clave</b> (le cierra las sesiones abiertas).",
      "<b>Editar pedidos</b> y <b>Anular pedidos</b> son permisos aparte: el primero deja corregir un pedido ya confirmado (cantidades, articulos, fecha) y el segundo darlo de baja. Los dos necesitan tambien el permiso <b>Pedido</b>.",
    ],
    tips: [
      "Para que la encargada corrija pedidos sin tocar precios: <b>Pedido</b> + <b>Editar pedidos</b>, sin <b>Lista de precios</b>. Los precios de cada linea quedan como estaban aunque los cambie en la pantalla.",
      "Todo cambio queda en <b>Auditoria</b> con el usuario que lo hizo.",
      "El que NO es admin no puede ajustar precios en el pedido ni ver esta pantalla.",
      "No podes quitarte tus propios permisos de admin (para no quedarte afuera).",
    ],
  },
  {
    permiso: "__admin",
    hash: "#/auditoria",
    titulo: "Auditoria",
    queEs:
      "Solo para el administrador: el registro de cambios. Cada alta, edicion, anulacion, pago o suba queda anotada con fecha, hora, usuario y detalle.",
    mock: `
      <div class="mock space-y-2">
        <table class="w-full"><tr class="text-masa-400"><td>Fecha</td><td>Usuario</td><td>Accion</td><td>Detalle</td></tr>
          <tr><td>16/07 14:32</td><td>${T("repartidor")}</td><td><span class="mock-btn-sec">crear pedido</span></td><td>Pedido #12 ${T("XXXXXXX")} total $ ${T("9.999")}</td></tr>
          <tr><td>16/07 10:15</td><td>admin</td><td><span class="mock-btn-sec">suba precios</span></td><td>Suba +5% en Lista22 (${T("97")} precios)</td></tr>
        </table>
      </div>`,
    pasos: [
      "Filtra por fecha con <b>Desde/Hasta</b> o los botones <b>Dia / Semana / Mes / Todo</b>.",
      "Cada fila dice <b>quien</b> hizo <b>que</b> y <b>cuando</b> (hora de Montevideo).",
    ],
    tips: ["Util para revisar 'quien cambio este precio' o 'quien anulo aquel pedido'."],
  },
  {
    permiso: null,
    hash: "#/clave",
    titulo: "Cambiar clave",
    queEs: "Cualquier usuario puede cambiar su propia clave desde su nombre en la barra de arriba.",
    mock: `
      <div class="mock space-y-1">
        <div class="mock-campo w-full">Clave actual: ••••••••</div>
        <div class="mock-campo w-full">Clave nueva: ••••••••</div>
        <div class="mock-campo w-full">Repetir clave nueva: ••••••••</div>
        <span class="mock-btn">Cambiar clave</span>
      </div>`,
    pasos: [
      "Tocá <b>tu nombre de usuario</b> arriba a la derecha (en el celular: abri el menu ☰ y toca 'Cambiar clave').",
      "Escribi la clave actual y la nueva dos veces (minimo 8 caracteres).",
    ],
    tips: ["Al cambiarla se cierran las sesiones en otros dispositivos (la tuya sigue abierta)."],
  },
];

export async function renderAyuda(contenedor: HTMLElement): Promise<void> {
  let sesion: Sesion | null = null;
  try {
    sesion = await api.get<Sesion>("/api/sesion");
  } catch {
    /* sin sesion no deberia llegar aca */
  }
  const puede = (s: SeccionAyuda): boolean => {
    if (!s.permiso) return true;
    if (sesion?.es_admin) return true;
    if (s.permiso === "__admin") return false;
    return (sesion?.permisos ?? []).includes(s.permiso);
  };
  const tienePermiso = (permiso: string): boolean =>
    sesion?.es_admin === true || (sesion?.permisos ?? []).includes(permiso);
  const puedeLinea = (l: Linea): boolean =>
    typeof l === "string" || sesion?.es_admin === true || (sesion?.permisos ?? []).includes(l.permiso);
  const texto = (l: Linea): string => (typeof l === "string" ? l : l.texto);
  const filtrar = (ls: Linea[]): string[] => ls.filter(puedeLinea).map(texto);

  const visibles = SECCIONES.filter(puede);

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina flex items-center gap-2"><span class="text-masa-600">${iconoAyuda}</span>Centro de ayuda</h1>
      <p class="subtitulo">Como usar cada parte del sistema, paso a paso. Las pantallas son ilustrativas: los datos sensibles van tapados.</p>
    </div>

    <div class="mb-4 flex flex-wrap gap-1">
      ${visibles
        .map(
          (s, i) => `<a href="#sec-${i}" class="btn-sec px-2 py-1 text-xs" data-idx="${i}">${s.titulo}</a>`,
        )
        .join("")}
    </div>

    <div class="mb-4 rounded-xl bg-crema-100 p-4 text-sm text-masa-700">
      <span class="mr-1 inline-block align-middle text-masa-600">${iconoPan}</span>
      <b>Como funciona el dia a dia:</b> a la tarde/noche se toman los pedidos cliente por cliente (cada uno con SU lista de precios).
      Cada pedido confirmado suma a la <b>produccion de manana</b> y anota la <b>COMPRA</b> en la cuenta del cliente.
      A la madrugada la cuadra produce lo del panel de Produccion —que ya viene sumado por <b>grupo</b> y convertido a <b>unidades</b>—;
      de dia se reparte con el remito y se registran devoluciones y pagos.
    </div>

    <div class="space-y-3">
      ${visibles
        .map(
          (s, i) => `
        <details id="sec-${i}" class="tarjeta p-0" ${visibles.length === 1 ? "open" : ""}>
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
            <span class="text-masa-500">${iconoRuta(s.hash)}</span>
            <span class="flex-1">${s.titulo}</span>
            ${s.permiso === "__admin" ? `<span class="rounded-full bg-masa-100 px-2 py-0.5 text-[10px] font-semibold normal-case text-masa-800">solo admin</span>` : ""}
          </summary>
          <div class="grid grid-cols-1 gap-4 px-4 pb-4 lg:grid-cols-2">
            <div>
              <p class="mb-3 text-sm text-masa-700">${s.queEs}</p>
              <h3 class="etiqueta">Paso a paso</h3>
              <ol class="mb-3 list-decimal space-y-1 pl-5 text-sm text-masa-800">
                ${filtrar(s.pasos).map((p) => `<li>${p}</li>`).join("")}
              </ol>
              <h3 class="etiqueta">Bueno saber</h3>
              <ul class="list-disc space-y-1 pl-5 text-sm text-masa-600">
                ${filtrar(s.tips).map((t) => `<li>${t}</li>`).join("")}
              </ul>
            </div>
            <div>
              <h3 class="etiqueta">Asi se ve la pantalla</h3>
              ${typeof s.mock === "string" ? s.mock : s.mock(tienePermiso)}
              <a href="${s.hash}" class="btn-sec mt-2 inline-flex text-xs">Ir a ${s.titulo} →</a>
            </div>
          </div>
        </details>`,
        )
        .join("")}
    </div>`;

  // Los chips del indice abren la seccion y bajan hasta ella.
  contenedor.querySelectorAll<HTMLAnchorElement>("[data-idx]").forEach((a) => {
    a.addEventListener("click", (ev) => {
      ev.preventDefault();
      const det = contenedor.querySelector<HTMLDetailsElement>(`#sec-${a.dataset.idx}`);
      if (det) {
        det.open = true;
        det.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });
}
